import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';

import { formatAmount, fractionToPercent } from 'helpers/helpers';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import { AccountDetailStore } from 'stores/account/accountDetailStore';

import useStyles from '../styles';

interface Props {
  store: AccountDetailStore;
  userMode: boolean;
}

const AccountInfo: React.FC<Props> = ({ store, userMode }) => {
  const { t } = useTranslation();
  const { data } = store;
  const classes = useStyles({});

  if (!data.summary) return null;

  return (
    <Grid container alignItems="center" spacing={0} className={classes.summary}>
      <Grid item container xs={12} sm={4} md={3} spacing={0} className={classes.infoBox}>
        <Grid item xs={12} className={classes.label}>
          {t('accountDetail_balanceValue')}:{' '}
        </Grid>
        <Grid className={`${classes.bigValue} ${classes.balance}`}>
          {data.detail ? <>{formatAmount(data.detail.balance, data.detail.currency)}<em>&nbsp;</em></> : <LoadingIndicator size={24} />}
        </Grid>
      </Grid>

      <Grid item container xs={12} sm={4} md={3} spacing={0} className={classes.infoBox}>
        <Grid item xs={12} className={classes.label}>
          {t('accountDetail_cashValue')}:{' '}
        </Grid>
        <Grid className={classes.bigValue}>
          {data.detail ? <>{formatAmount(data.detail.cash, data.detail.currency)}<em>&nbsp;</em></> : <LoadingIndicator size={24} />}
        </Grid>
      </Grid>

      <Grid item container xs={12} sm={4} md={3} spacing={0} className={classes.infoBox}>
        <Grid item xs={12} className={classes.label}>
          {t('accountDetail_profitValue')}:{' '}
        </Grid>
        <Grid item className={classes.bigValue}>
          {data.detail ? (
            <>
              <span className={`${data.detail.profit.value >= 0 ? 'positive' : 'negative'}`}>
                {formatAmount(data.detail.profit, data.detail.currency)}
                {' '}
              </span>
              {data.detail.yearlyProfit !== undefined && (
                <em>
                  ({fractionToPercent(data.detail.yearlyProfit, 2)}
                  {' '}p. a.)
                </em>
              )}
            </>
          ) : (
            <LoadingIndicator size={24} />
          )}
        </Grid>
      </Grid>

      {!userMode && (
        <Grid item container xs={12} md={3} spacing={0} className={`${classes.infoBox} ${classes.hideBoxSm}`}>
          <Grid item xs={12} className={classes.label}>
            {t('accountDetail_feesPaid')}:{' '}
          </Grid>
          <Grid item className={classes.bigValue}>
            {data.detail ? (
              <>
                {formatAmount(data.detail.feePaid, data.detail.currency)}
                {data.detail.feePaidAll === false ? (
                  <>
                    {' '}
                    ({t('general_from')} {formatAmount(data.detail.feeTotal, data.detail.currency)})
                  </>
                ) : (
                  ''
                )}
                <em>&nbsp;</em>
              </>
            ) : (
              <LoadingIndicator size={24} />
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default observer(AccountInfo);
